import React, { useEffect } from "react";
import { ConfigProvider, Flex, Image, Layout, Typography } from "antd";
import { authDarkTheme, authLightTheme } from "../theme/authTheme";
import { controlTokens } from "../theme/controlTokens";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { themeState } from "../atom";
import { IoMdArrowRoundBack } from "react-icons/io";
import logo from "../assets/large_logo.png";

/**
 * The frame every auth screen sits in: one centred card, nothing else.
 *
 * It used to be a half-and-half split — the form on the left, a full-height
 * illustration on the right. The illustration was decoration on a screen whose
 * entire job is two fields and a button: it pushed the form into a 150px-padded
 * column that collapsed to 10px below 1500px, and it vanished altogether on
 * mobile, so the layout nobody saw was the one being designed around.
 *
 * Centring it means one layout at every width, the form reads as the subject of
 * the page, and Login, Register, Forgot, OTP and Set-password all look like
 * members of the same family.
 */
const AuthLayout = ({
  children,
  title,
  backToLogin = false,
  /**
   * Kept so the five existing screens keep compiling. It used to force a
   * near-viewport-height inner column, which is exactly what a centred card
   * must not do, so it is accepted and ignored rather than passed through.
   */
  customCenterClassName = "",
  marginTop = "20px",
}) => {
  const navigate = useNavigate();
  const [theme] = useRecoilState(themeState);

  useEffect(() => {
    localStorage.setItem("theme", JSON.stringify(theme));
  }, [theme]);

  useEffect(() => {
    const accessToken = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (accessToken && user) {
      navigate('/roles');
    }
  }, [navigate]);

  const dark = theme === "dark" || (Array.isArray(theme) && theme[0] === "dark");

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#29b8e3",
          // 40px controls everywhere. See theme/controlTokens.js.
          ...controlTokens,
        },
        components: dark ? authDarkTheme : authLightTheme,
      }}
    >
      <Layout className={`auth-shell ${dark ? "auth-shell--dark" : ""}`}>
        <Layout.Content>
          {/* The card is the page. Vertical centring is on the shell, with
              padding rather than a fixed height, so a long form (Register) grows
              downwards and scrolls instead of being clipped. */}
          <div className="auth-shell__inner">
            <div className="auth-card">
              {backToLogin && (
                <Link to={"/login"} className="auth-card__back">
                  <Flex gap={"small"} align="center">
                    <IoMdArrowRoundBack size={18} />
                    <Typography.Text>Back to Login</Typography.Text>
                  </Flex>
                </Link>
              )}

              <Flex vertical align="center" style={{ marginBottom: marginTop }}>
                <Image src={logo} style={{ width: "140px" }} preview={false} />
                <Typography.Title level={3} className="auth-card__title">
                  {title}
                </Typography.Title>
              </Flex>

              {children}
            </div>
          </div>
        </Layout.Content>
      </Layout>
    </ConfigProvider>
  );
};

export default AuthLayout;
